import React, { useEffect, useMemo, useState } from "react";
import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
// Relative: these resolve for real under Jest, where the "@" alias does not exist.
import { OperatorProvider } from "./OperatorContext";
import { NoAccessPanel } from "./NoAccess";
import { mayOpen } from "./screenAccess";
import { practiceConsoleHref } from "./practiceConsole";
import SuperAdminSidebar, { activeSectionKey } from "./SuperAdminSidebar";
import { 
  BarChart3, Users, Package, CreditCard, Activity, DollarSign, Globe,
  Building2, PieChart, Megaphone, Tag, Sparkles, Workflow, FileText, 
  LayoutTemplate, UserCog, History, Plug, Settings, Gift, Star, 
  Moon, UserPlus, HelpCircle, Bell, MessageSquare, Home, GraduationCap, Layers,
  Headphones, Wallet, Menu, Table2, Landmark
} from "lucide-react";

// The sidebar, in order. `labelKey` / `titleKey` are `superadmin` dictionary keys: every label
// and group title is read in the operator's language. Which entries an operator sees is
// screenAccess.js's decision (the same table that gates a direct visit).
const NAV_SECTIONS = [
  { key: "top", items: [
    { to: "/super-admin", end: true, icon: Home, labelKey: "nav.overview", top: true },
  ] },
  { key: "management", titleKey: "sa.nav.section.management", items: [
    { to: "/super-admin/tenants", icon: Users, labelKey: "nav.tenants" },
    // Moderation, not finance: hiding a review is a decision about a practice's public record.
    // `tenants.read` to look, `tenants.write` to hide.
    { to: "/super-admin/reviews", icon: Star, labelKey: "nav.reviews" },
    { to: "/super-admin/custom-domains", icon: Globe, labelKey: "nav.customDomains" },
    { to: "/super-admin/plans", icon: Package, labelKey: "nav.plans" },
    { to: "/super-admin/plan-features", icon: Table2, labelKey: "nav.planFeatures" },
    { to: "/super-admin/catalog", icon: Layers, labelKey: "nav.catalog" },
    { to: "/super-admin/team", icon: UserCog, labelKey: "nav.team" },
  ] },
  { key: "finance", titleKey: "sa.nav.section.finance", collapsible: true, items: [
    { to: "/super-admin/finance", icon: DollarSign, labelKey: "nav.finance" },
    { to: "/super-admin/revenue", icon: PieChart, labelKey: "nav.platformRevenue" },
    { to: "/super-admin/transactions", icon: CreditCard, labelKey: "nav.transactions" },
    { to: "/super-admin/client-transactions", icon: Wallet, labelKey: "nav.clientTransactions" },
    { to: "/super-admin/gateway", icon: Landmark, labelKey: "nav.gateway" },
    { to: "/super-admin/affiliate", icon: Gift, labelKey: "nav.affiliate" },
    { to: "/super-admin/payments", icon: Plug, labelKey: "nav.payments" },
  ] },
  { key: "hq", titleKey: "sa.nav.section.hq", collapsible: true, items: [
    { to: "/super-admin/hq/customers", icon: Building2, labelKey: "nav.hqCustomers" },
    { to: "/super-admin/hq/finance", icon: DollarSign, labelKey: "nav.hqFinance" },
    { to: "/super-admin/hq/service-leads", icon: Headphones, labelKey: "nav.hqServiceLeads" },
    { to: "/super-admin/hq/social", icon: Megaphone, labelKey: "nav.hqSocial" },
    { to: "/super-admin/hq/gift-promo", icon: Tag, labelKey: "nav.hqGiftPromo" },
    { to: "/super-admin/hq/ai", icon: Sparkles, labelKey: "nav.hqAi" },
    { to: "/super-admin/hq/automation", icon: Workflow, labelKey: "nav.hqAutomation" },
    { to: "/super-admin/hq/blog", icon: FileText, labelKey: "nav.hqBlog" },
    { to: "/super-admin/hq/site", icon: LayoutTemplate, labelKey: "nav.hqSite" },
  ] },
  { key: "system", titleKey: "sa.nav.section.system", collapsible: true, items: [
    { to: "/super-admin/settings", icon: Settings, labelKey: "nav.settings" },
    { to: "/super-admin/global", icon: Globe, labelKey: "nav.global" },
    { to: "/super-admin/global/learn-telehealth", icon: GraduationCap, labelKey: "nav.globalLearn" },
    { to: "/super-admin/audit", icon: History, labelKey: "nav.audit" },
    { to: "/super-admin/activity", icon: Activity, labelKey: "nav.activity" },
    { to: "/super-admin/integrations", icon: Plug, labelKey: "nav.integrations" },
    { to: "/super-admin/webhooks", icon: Plug, labelKey: "nav.webhooks" },
    { to: "/super-admin/platform-credentials", icon: Plug, labelKey: "nav.platformCredentials" },
  ] },
];

/** `GET /super-admin/me` as the provider shapes it, for the access checks below. */
const operatorOf = (me) => {
  const granted = new Set(Array.isArray(me?.permissions) ? me.permissions : []);
  return { isSuperAdmin: me?.is_super_admin === true, can: (p) => granted.has(p) };
};

const NO_OP = () => {};

/** Loads the operator, then mounts the shell once it is known (the shell's first state needs it). */
export default function SuperAdminLayout() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Every active platform operator gets in (Super-Admin, Finance, Support, Read only); what each
  // may see is decided per screen below. `/me` is the only read: it carries the permissions.
  useEffect(() => {
    api.get("/super-admin/me").then((r) => {
      setMe(r.data);
      setLoading(false);
      if (!r.data?.is_operator) navigate("/super-admin/login");
    }).catch(() => {
      setLoading(false);
      navigate("/super-admin/login");
    });
  }, [navigate]);

  if (loading) {
    return <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
    </div>;
  }

  if (!me?.is_operator) return null; // on its way to the login page

  return <SuperAdminShell me={me} />;
}

function SuperAdminShell({ me }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const t = useT("superadmin");

  const operator = useMemo(() => operatorOf(me), [me]);
  const sections = useMemo(() => NAV_SECTIONS
    .map((section) => ({ ...section, items: section.items.filter((item) => mayOpen(item.to, operator)) }))
    .filter((section) => section.items.length > 0), [operator]);

  // The section that holds the open screen starts expanded (no collapsed-then-open flicker on a
  // deep link or reload), and every later navigation opens the section it enters. That is additive:
  // it never collapses another section, and a section the operator collapsed by hand stays collapsed
  // until the route enters a section again.
  const activeKey = activeSectionKey(sections, pathname);
  const [expandedSections, setExpandedSections] = useState(() => ({
    hq: false, system: false, finance: false, ...(activeKey ? { [activeKey]: true } : {}),
  }));
  useEffect(() => {
    if (activeKey) setExpandedSections((prev) => (prev[activeKey] ? prev : { ...prev, [activeKey]: true }));
  }, [activeKey, pathname]);

  const toggleSection = (sec) => {
    setExpandedSections((prev) => ({ ...prev, [sec]: !prev[sec] }));
  };

  const onTenantSwitch = (slug) => {
    const href = practiceConsoleHref(slug);
    if (href) window.location.href = href;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/super-admin/login");
  };

  // The screen for the address in the bar: the routed page, or why it is not shown. An operator
  // who cannot read the Overview (super admin only) lands on the first screen they can.
  const firstScreen = sections[0]?.items[0]?.to;
  const isIndex = pathname.replace(/\/+$/, "") === "/super-admin";
  let screen = <Outlet />;
  if (!mayOpen(pathname, operator)) {
    screen = isIndex && firstScreen ? <Navigate to={firstScreen} replace /> : <NoAccessPanel />;
  }

  const sidebarProps = {
    sections, expandedSections, onToggleSection: toggleSection, isSuperAdmin: operator.isSuperAdmin,
    onTenantSwitch, onLogout: logout,
  };

  return (
    <div className="min-h-screen bg-[#f7f8f9] text-slate-800 flex flex-col md:flex-row font-sans overflow-x-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-[280px] bg-white border-r border-slate-100 flex-col transition-all h-screen overflow-hidden shrink-0" data-lenis-prevent="true">
        <SuperAdminSidebar {...sidebarProps} variant="desktop" onNavigate={NO_OP} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="relative z-10 w-80 max-w-[85vw] bg-white h-full shadow-2xl flex flex-col">
            <SuperAdminSidebar {...sidebarProps} variant="mobile" onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen md:h-screen md:overflow-hidden min-w-0">
        <header data-testid="sa-topbar" className="h-16 md:h-[76px] flex-shrink-0 flex items-center justify-between px-4 md:px-8 bg-white md:bg-transparent border-b md:border-b-0 border-slate-100">
           <div className="flex items-center gap-3 md:gap-6">
             <button data-testid="sa-mobile-menu-open" onClick={() => setMobileOpen(true)} className="md:hidden p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50">
               <Menu className="w-5 h-5" />
             </button>
             {/* No location chooser and no global search here: both used to be template
                 placeholders with no handler and nothing behind them (the platform has no
                 locations; no endpoint searches the whole console), printed in English in
                 every language. Each screen carries its own working search. */}
           </div>
           
           {/* The address is printed whole: it is cut (whole value on `title`) only when the bar
               itself runs out of room, which is why every box down to it may shrink. */}
           <div className="flex items-center gap-2 sm:gap-4 min-w-0">
             <div className="hidden sm:flex flex-col items-end mr-2 min-w-0">
               <span data-testid="sa-topbar-email" title={me?.email} className="text-xs sm:text-sm font-bold text-slate-800 truncate max-w-full">{me?.email}</span>
               <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{t(`team.role.${me.role}`, undefined, me.role || "")}</span>
             </div>
             <img src={me?.picture || "https://i.pravatar.cc/150?u=admin"} alt="Profile" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover shadow-sm border border-slate-200 shrink-0" />
           </div>
        </header>

        <div className="flex-1 min-h-0 p-4 sm:p-6 md:p-8 pt-2 overflow-y-auto" data-lenis-prevent="true">
          <OperatorProvider me={me}>{screen}</OperatorProvider>
        </div>
      </main>
    </div>
  );
}
